import { useEffect, useRef } from 'react'
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import { Radar } from 'react-chartjs-2'

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
)

function RadarChart({ domains, scores }) {
  const chartRef = useRef(null)
  

  // Fallback for when Chart.js fails to load or data is missing
  const FallbackChart = () => (
    <div className="grid grid-cols-3 gap-2 p-4">
      {(domains || []).map((domain, index) => (
        <div 
          key={index}
          className="text-center p-2 bg-gray-50 rounded-lg border-l-4 border-cedec-blue group relative cursor-help"
          title={domain.name} // Native HTML tooltip as fallback
        >
          <div className="text-xs font-semibold mb-2 text-gray-700">
            {domain.shortName || domain.name || 'N/A'}
          </div>
          <div className="h-12 flex items-end justify-center">
            <div 
              className="w-4 bg-gradient-to-t from-red-500 via-yellow-500 to-green-500 rounded-sm relative"
              style={{ height: '100%' }}
            >
              <div 
                className="absolute right-0 w-6 h-1 bg-cedec-blue rounded-sm"
                style={{ 
                  top: `${100 - ((scores?.[index] || 0) * 10)}%`,
                  transform: 'translateY(-50%)'
                }}
              />
            </div>
          </div>
          <div className="text-sm font-bold text-cedec-blue mt-1">
            {scores?.[index] || 0}
          </div>
          
          {/* Custom tooltip for fallback chart */}
          {domain.name !== domain.shortName && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              {domain.name}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
            </div>
          )}
        </div>
      ))}
    </div>
  )

  // Handle missing or empty data
  if (!domains || !scores || domains.length === 0 || scores.length === 0) {
    console.warn('🎯 RadarChart: Missing or empty data, showing fallback')
    return <FallbackChart />
  }

  // Convert null values to 0 for chart display
  const chartScores = scores.map(score => score !== null && score !== undefined ? score : 0)

  const data = {
    labels: domains.map(d => d.shortName),
    datasets: [
      {
        label: 'Niveau de maîtrise',
        data: chartScores,
        backgroundColor: 'rgba(44, 90, 160, 0.2)',
        borderColor: 'rgba(44, 90, 160, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(44, 90, 160, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const domain = domains[context.dataIndex]
            const fullName = domain?.name || context.label
            const score = context.parsed.r
            return `${fullName}: ${score}/10`
          },
          title: function(context) {
            if (context.length > 0) {
              const domain = domains[context[0].dataIndex]
              return domain?.name || context[0].label
            }
            return ''
          }
        }
      }
    },
    scales: {
      r: {
        angleLines: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        suggestedMin: 0,
        suggestedMax: 10,
        ticks: {
          stepSize: 2,
          color: '#666',
          backdropColor: 'transparent',
          font: {
            size: 10,
          },
        },
        pointLabels: {
          color: '#333',
          font: {
            size: 11,
            weight: 'bold',
          },
          callback: function(value, index) {
            // Return the short name for display
            return domains[index]?.shortName || value
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
    elements: {
      line: {
        tension: 0.1,
      },
    },
    animation: {
      duration: 800,
      easing: 'easeInOutQuart',
    },
  }


  try {
    return (
      <div className="relative" style={{ height: '250px' }}>
        <Radar 
          ref={chartRef}
          data={data} 
          options={options}
        />
      </div>
    )
  } catch (error) {
    console.warn('Chart.js fallback activated:', error)
    return <FallbackChart />
  }
}

export default RadarChart
